import {ArgumentType, BlockType} from 'scratch-vm';
import formatMessage from 'format-message';

/**
 * Icon svg for the AI extension block menu
 * @type {string}
 */
const aiSvg = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40"><rect width="40" height="40" rx="8" fill="#7C3AED"/><text x="20" y="28" text-anchor="middle" font-size="18" font-weight="bold" fill="white" font-family="sans-serif">AI</text></svg>';
const menuIconURI = `data:image/svg+xml;base64,${typeof btoa !== 'undefined' ? btoa(aiSvg) : ''}`;

const blockIconURI = menuIconURI;

/**
 * How long to wait before timing out requests to AI backend.
 * @type {number}
 */
const SERVER_TIMEOUT_MS = 30000;

/**
 * Maximum number of messages to keep in conversation history per sprite.
 * @type {number}
 */
const MAX_HISTORY_LENGTH = 10;

/**
 * Maximum prompt length in characters.
 * @type {number}
 */
const MAX_PROMPT_LENGTH = 500;

/**
 * Class for the AI extension in Scratch 3.0.
 * Allows sprites to communicate with an LLM via the backend API.
 */
class Scratch3AiBlocks {
    constructor (runtime) {
        this.runtime = runtime;
        this._lastAnswer = {};
        this._systemPrompt = {};
        this._conversationHistory = {};
        this._requestInProgress = {};
        this._aiStatus = null;
        this._aiStatusTimestamp = 0;

        // Reset conversation on green flag
        if (this.runtime) {
            this.runtime.on('PROJECT_START', () => {
                this._lastAnswer = {};
                this._conversationHistory = {};
            });
        }
    }

    getInfo () {
        return {
            id: 'aiBlocks',
            name: formatMessage({
                id: 'ai.categoryName',
                default: 'AI',
                description: 'Label for the AI extension category'
            }),
            menuIconURI: menuIconURI,
            blockIconURI: blockIconURI,
            color1: '#7C3AED',
            color2: '#6D28D9',
            color3: '#5B21B6',
            blocks: [
                {
                    opcode: 'askAI',
                    blockType: BlockType.COMMAND,
                    text: formatMessage({
                        id: 'ai.askAI',
                        default: 'ask AI [PROMPT]',
                        description: 'Block to send a prompt to AI and wait for response'
                    }),
                    arguments: {
                        PROMPT: {
                            type: ArgumentType.STRING,
                            defaultValue: formatMessage({
                                id: 'ai.askAI.defaultPrompt',
                                default: 'Hello!',
                                description: 'Default prompt for AI block'
                            })
                        }
                    }
                },
                {
                    opcode: 'getAnswer',
                    blockType: BlockType.REPORTER,
                    text: formatMessage({
                        id: 'ai.getAnswer',
                        default: 'AI answer',
                        description: 'Block to get the last AI response'
                    })
                },
                '---',
                {
                    opcode: 'setSystemPrompt',
                    blockType: BlockType.COMMAND,
                    text: formatMessage({
                        id: 'ai.setSystemPrompt',
                        default: 'set AI personality to [PROMPT]',
                        description: 'Block to set the system prompt / AI personality'
                    }),
                    arguments: {
                        PROMPT: {
                            type: ArgumentType.STRING,
                            defaultValue: formatMessage({
                                id: 'ai.setSystemPrompt.default',
                                default: 'You are a friendly robot.',
                                description: 'Default system prompt'
                            })
                        }
                    }
                },
                {
                    opcode: 'clearConversation',
                    blockType: BlockType.COMMAND,
                    text: formatMessage({
                        id: 'ai.clearConversation',
                        default: 'clear AI conversation',
                        description: 'Block to clear conversation history'
                    })
                },
                '---',
                {
                    opcode: 'isReady',
                    blockType: BlockType.BOOLEAN,
                    text: formatMessage({
                        id: 'ai.isReady',
                        default: 'AI is ready?',
                        description: 'Block to check if AI is available'
                    })
                }
            ]
        };
    }

    _getTargetId (util) {
        return util.target ? util.target.id : '_stage';
    }

    _getHistory (targetId) {
        if (!this._conversationHistory[targetId]) {
            this._conversationHistory[targetId] = [];
        }
        return this._conversationHistory[targetId];
    }

    _addToHistory (targetId, role, content) {
        const history = this._getHistory(targetId);
        history.push({role, content});
        while (history.length > MAX_HISTORY_LENGTH) {
            history.shift();
        }
    }

    _fetchWithTimeout (url, options) {
        return new Promise((resolve, reject) => {
            const timer = setTimeout(() => {
                reject(new Error('AI request timed out'));
            }, SERVER_TIMEOUT_MS);

            fetch(url, options)
                .then(response => {
                    clearTimeout(timer);
                    resolve(response);
                })
                .catch(err => {
                    clearTimeout(timer);
                    reject(err);
                });
        });
    }

    askAI (args, util) {
        const prompt = String(args.PROMPT || '').substring(0, MAX_PROMPT_LENGTH);
        const targetId = this._getTargetId(util);

        if (!prompt) {
            this._lastAnswer[targetId] = '';
            return;
        }

        if (this._requestInProgress[targetId]) {
            return;
        }

        this._requestInProgress[targetId] = true;

        const systemPrompt = this._systemPrompt[targetId] || null;
        const history = this._getHistory(targetId);

        return this._fetchWithTimeout('/api/ai/chat', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            credentials: 'same-origin',
            body: JSON.stringify({
                prompt: prompt,
                systemPrompt: systemPrompt,
                conversationHistory: history
            })
        })
            .then(response => {
                if (!response.ok) {
                    return response.json().then(data => {
                        throw new Error(data.error?.message || 'AI request failed');
                    });
                }
                return response.json();
            })
            .then(data => {
                const answer = data.answer || '';
                this._lastAnswer[targetId] = answer;
                this._addToHistory(targetId, 'user', prompt);
                this._addToHistory(targetId, 'assistant', answer);
            })
            .catch(err => {
                console.warn('AI request error:', err);
                this._lastAnswer[targetId] = '';
            })
            .finally(() => {
                this._requestInProgress[targetId] = false;
            });
    }

    getAnswer (args, util) {
        const targetId = this._getTargetId(util);
        return this._lastAnswer[targetId] || '';
    }

    setSystemPrompt (args, util) {
        const targetId = this._getTargetId(util);
        this._systemPrompt[targetId] = String(args.PROMPT || '');
    }

    clearConversation (args, util) {
        const targetId = this._getTargetId(util);
        this._conversationHistory[targetId] = [];
        this._lastAnswer[targetId] = '';
    }

    isReady () {
        const now = Date.now();
        if (this._aiStatus && (now - this._aiStatusTimestamp) < 10000) {
            return this._aiStatus.ready || false;
        }

        return fetch('/api/ai/status', {
            credentials: 'same-origin'
        })
            .then(response => {
                if (!response.ok) return false;
                return response.json();
            })
            .then(data => {
                this._aiStatus = data;
                this._aiStatusTimestamp = now;
                return data.ready || false;
            })
            .catch(() => false);
    }
}

export default Scratch3AiBlocks;
